import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { useToast } from '../components/common/Toast';
import { loginUrlFor } from '../utils/loginRedirect';
import { clearAppPreview } from '../utils/appPreview';
import { keepCodes } from '../utils/joinFlow';
import {
  deriveWrappingKey,
  generateKeyPair,
  exportPublicKey,
  wrapPrivateKey,
  unwrapPrivateKey,
  deriveEscrowKey,
  wrapKeyForEscrow,
} from '../utils/fieldEncryption';

const AuthContext = createContext(null);

// Cross-tab logout signalling: when one tab logs out, every other tab of this
// app drops its session immediately instead of lingering in a stale logged-in
// state. Guarded for environments without BroadcastChannel.
const authChannel =
  typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('paradigm-auth') : null;

// THE SESSION HYDRATES WARM (owner call 2026-08-22): the beige "Loading…"
// wall on every refresh was this provider blocking first paint on a /auth/me
// round trip. The last confirmed identity is snapshotted here, so a load that
// still holds a token paints the app immediately and the fetch revalidates
// underneath — a 401 still tears the session down exactly as before. Identity
// only; no tokens or key material beyond what was already stored.
const CACHED_USER_KEY = 'nucleus:me';
const readCachedUser = () => {
  try { return JSON.parse(localStorage.getItem(CACHED_USER_KEY)); } catch { return null; }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => (localStorage.getItem('token') ? readCachedUser() : null));
  const [loading, setLoading] = useState(() => !(localStorage.getItem('token') && readCachedUser()));

  // encKeys holds in-memory key material — never persisted to localStorage/sessionStorage.
  // Shape: { publicKey: CryptoKey, privateKey: CryptoKey, wrappingKey: CryptoKey }
  //   publicKey   → encrypt fields (same key available to 3PAs via public endpoint)
  //   privateKey  → decrypt v2 ciphertext
  //   wrappingKey → decrypt legacy v1 ciphertext + protect stored private key
  const [encKeys, setEncKeys] = useState(null);
  const toast = useToast();

  // pendingRecoveryCodes: plaintext codes shown once after first-login key setup.
  // Cleared by calling clearPendingRecoveryCodes() once the user has acknowledged them.
  const [pendingRecoveryCodes, setPendingRecoveryCodes] = useState(null);
  // Whether existing recovery codes have escrow entries (false → show warning in Settings).
  const [recoveryCodesHaveEscrow, setRecoveryCodesHaveEscrow] = useState(true);

  // Single source of truth for tearing down local auth state. Used by both the
  // user-initiated logout() and the forced/expiry path so they never drift.
  const clearLocalAuth = useCallback(() => {
    api.setToken(null);
    api.setRefreshToken(null);
    api.setApiKey(null);
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('apiKey');
    localStorage.removeItem(CACHED_USER_KEY);
    sessionStorage.removeItem('activeSession');
    // Otherwise a preview tab stays trapped on /app/:id/graph across a
    // sign-out + re-login — the flag outlives the session it was scoped to.
    clearAppPreview();
    keepCodes(null);
    setUser(null);
    setEncKeys(null); // Wipe all key material from memory
  }, []);

  // Handle forced logout (when auth expires or the session is revoked elsewhere)
  //
  // CARRIES WHERE IT HAPPENED. The consent page is not behind ProtectedRoute —
  // it mounts for anyone and then calls the one JWT-required endpoint it needs
  // to render (`/authorize/preview`). So a logged-out visitor to
  // `/authorize?client_id=…&redirect_uri=…` arrives here before a single pixel
  // of consent exists, and a bare `/login` drops the app's callback on the
  // floor. See utils/loginRedirect.js.
  const forceLogout = useCallback((showExpiredMessage = true) => {
    clearLocalAuth();
    window.location.href = loginUrlFor(window.location, { expired: showExpiredMessage });
  }, [clearLocalAuth]);

  const fetchUser = useCallback(async () => {
    try {
      const userData = await api.getMe();
      setUser(userData);
      try { localStorage.setItem(CACHED_USER_KEY, JSON.stringify(userData)); } catch { /* quota */ }
      return userData;
    } catch (error) {
      // Only a REJECTED session tears down (401/403). A transient failure —
      // server restarting, network blip — keeps the hydrated identity, or the
      // warm boot above would flash the app and then bounce it to /login.
      if (error?.status === 401 || error?.status === 403 || error?.isAuthError) {
        api.setToken(null);
        localStorage.removeItem('token');
        localStorage.removeItem(CACHED_USER_KEY);
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Consumes a `?code=` handoff code minted by another first-party frontend
  // (developer portal, admin) via POST /auth/handoff — the arrival side
  // of a flow whose mint-and-redirect half already existed but had no
  // consumer anywhere in either frontend. Stores tokens exactly like login()
  // does, then strips the code from the URL so a refresh never re-submits it.
  //
  // Captured once, synchronously, before any effect has a chance to mutate
  // the URL — StrictMode double-invokes both the render and the mount effect
  // in dev, and checking `window.location.search` fresh inside the effect
  // would see the code already stripped by the first invocation, silently
  // skipping the guard below on the second one.
  const [hadHandoffCode] = useState(() => new URLSearchParams(window.location.search).has('code'));
  const exchangeHandoffCodeIfPresent = useCallback(async () => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    if (!code) return false;

    params.delete('code');
    const cleanSearch = params.toString();
    const cleanUrl = window.location.pathname + (cleanSearch ? `?${cleanSearch}` : '') + window.location.hash;
    window.history.replaceState({}, '', cleanUrl);

    // A tab that's already signed in never silently swaps sessions for
    // whoever the code belongs to — a handoff code has no binding to the
    // browser that presents it, so an already-logged-in tab opening a link
    // carrying someone else's `?code=` must not become their session. The
    // intended flow (a fresh tab from the dev-portal handoff) never hits
    // this: there is no token yet.
    if (localStorage.getItem('token')) return false;

    try {
      const response = await api.exchangeHandoffCode(code);
      const token = response.access_token;
      if (!token) return false;
      api.setToken(token);
      localStorage.setItem('token', token);
      if (response.refresh_token) {
        api.setRefreshToken(response.refresh_token);
      }
      sessionStorage.setItem('activeSession', 'true');
      return true;
    } catch (e) {
      // Invalid/expired/already-used — fall through to the normal
      // unauthenticated path rather than surfacing a crash on arrival.
      console.warn('Handoff code exchange failed:', e?.message || e);
      return false;
    }
  }, []);

  // StrictMode's second mount effect must not independently decide "no token
  // yet, stay logged out" while the first mount's exchange is still
  // in-flight — that race is exactly what bounced a real, successful handoff
  // through /login and on to /home instead of the intended destination. Only
  // the invocation that wins this ref proceeds; the duplicate does nothing at
  // all, leaving the winner as the sole owner of resolving loading/user.
  const handoffClaimedRef = useRef(false);
  useEffect(() => {
    (async () => {
      if (hadHandoffCode) {
        if (handoffClaimedRef.current) return;
        handoffClaimedRef.current = true;
        await exchangeHandoffCodeIfPresent();
      }
      const token = localStorage.getItem('token');

      if (token) {
        api.setToken(token);
        fetchUser();
      } else {
        setLoading(false);
      }
    })();

    const handleAuthExpired = (event) => {
      const showExpiredMessage = event.detail?.showExpiredMessage ?? true;
      forceLogout(showExpiredMessage);
    };
    window.addEventListener('auth:expired', handleAuthExpired);

    // Cross-tab: another tab logged out → drop this tab's session too.
    const handleChannelMessage = (event) => {
      if (event.data?.type === 'logout') forceLogout(false);
    };
    authChannel?.addEventListener('message', handleChannelMessage);

    // Idle poll: when the tab regains focus, confirm the session is still live.
    // A revoked session (logged out elsewhere / force-logged-out by an app)
    // returns 401 here, so we drop it without waiting for the next API call.
    const handleVisibility = async () => {
      if (document.visibilityState !== 'visible') return;
      if (!localStorage.getItem('token')) return;
      try {
        await api.getSessionStatus();
      } catch (e) {
        if (e?.status === 401) forceLogout(false);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.removeEventListener('auth:expired', handleAuthExpired);
      authChannel?.removeEventListener('message', handleChannelMessage);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [forceLogout, fetchUser, exchangeHandoffCodeIfPresent, hadHandoffCode]);

  const _setupEncKeys = async (password, userId, loginResp) => {
    try {
      const wrappingKey = await deriveWrappingKey(password, String(userId));
      const { enc_public_key, enc_private_key_wrapped } = loginResp;

      let privateKey, publicKey;

      if (enc_public_key && enc_private_key_wrapped) {
        // Existing key pair — unwrap private key in memory
        const pubBytes = Uint8Array.from(
          atob(enc_public_key.replace(/-/g, '+').replace(/_/g, '/')),
          c => c.charCodeAt(0)
        );
        publicKey = await crypto.subtle.importKey(
          'raw', pubBytes, { name: 'ECDH', namedCurve: 'P-256' }, true, []
        );
        privateKey = await unwrapPrivateKey(enc_private_key_wrapped, wrappingKey);
      } else {
        // First login — generate key pair, wrap private key, upload
        const kp = await generateKeyPair();
        privateKey = kp.privateKey;
        publicKey = kp.publicKey;

        const pubBytes = await exportPublicKey(publicKey);
        const wrapped = await wrapPrivateKey(privateKey, wrappingKey);
        const pubB64 = btoa(String.fromCharCode(...pubBytes))
          .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');

        // Derive escrow entries for each setup recovery code (returned on first login only)
        const setupCodes = loginResp.setup_recovery_codes || [];
        const recoveryKeyEscrow = await Promise.all(
          setupCodes.map(async ({ id, code }) => {
            const escrowKey = await deriveEscrowKey(code, String(userId));
            const encEscrow = await wrapKeyForEscrow(privateKey, escrowKey);
            return { code_id: id, enc_key_escrow: encEscrow };
          })
        );

        await api.request('/auth/enc-keys', {
          method: 'PUT',
          body: JSON.stringify({
            enc_public_key: pubB64,
            enc_private_key_wrapped: wrapped,
            recovery_key_escrow: recoveryKeyEscrow,
          }),
        });

        if (setupCodes.length) {
          const codes = setupCodes.map(c => c.code);
          setPendingRecoveryCodes(codes);
          // Their escrow is already uploaded: a reload before they are shown
          // must not lose them (pages/join/JoinCodes reads them back).
          keepCodes(codes);
        }
      }

      setRecoveryCodesHaveEscrow(loginResp.recovery_codes_have_escrow ?? true);
      setEncKeys({ publicKey, privateKey, wrappingKey });
    } catch (e) {
      console.warn('Failed to set up encryption keys:', e);
    }
  };

  // Re-derive the in-memory keypair on a RESUMED session (valid token, but no password
  // entered this load → encKeys is null) WITHOUT a full re-login. The authorize flow
  // calls this when it must provision an app's delegated key but the key isn't unlocked.
  // Password never leaves the client. Returns the keys object on success, or null.
  const unlockEncKeys = useCallback(async (password) => {
    if (encKeys?.privateKey) return encKeys;
    if (!user?.id) return null;
    try {
      const resp = await api.request('/auth/enc-keys', { method: 'GET' });
      const { enc_public_key, enc_private_key_wrapped } = resp || {};
      if (!enc_public_key || !enc_private_key_wrapped) return null;
      const wrappingKey = await deriveWrappingKey(password, String(user.id));
      const pubBytes = Uint8Array.from(
        atob(enc_public_key.replace(/-/g, '+').replace(/_/g, '/')),
        c => c.charCodeAt(0)
      );
      const publicKey = await crypto.subtle.importKey(
        'raw', pubBytes, { name: 'ECDH', namedCurve: 'P-256' }, true, []
      );
      const privateKey = await unwrapPrivateKey(enc_private_key_wrapped, wrappingKey);
      const keys = { publicKey, privateKey, wrappingKey };
      setEncKeys(keys);
      return keys;
    } catch (e) {
      console.warn('unlockEncKeys failed (wrong password or no key material):', e);
      return null;
    }
  }, [encKeys, user]);

  // A sign-in's answer becomes the session: the tokens, the account's keys
  // (unwrapped, or made on a first sign-in, which mints the recovery codes) and
  // the user. Shared by login() and by the phone step (pages/join/JoinPhone),
  // whose /auth/phone/check answers exactly as /auth/login does. Without the
  // password the keys cannot be derived; they are then left for the next
  // sign-in, which sees no key pair and mints the codes afresh.
  const completeLogin = async (response, password) => {
    const token = response.access_token || response.token;
    if (token) {
      api.setToken(token);
      localStorage.setItem('token', token);
      if (response.refresh_token) {
        api.setRefreshToken(response.refresh_token);
      }
      sessionStorage.setItem('activeSession', 'true');
    }

    if (password && response.user?.id) {
      await _setupEncKeys(password, response.user.id, response);
    }

    // The sign-in's `user` is thin (no profile id); /auth/me's is whole, and
    // the join reads the profile id off it to name the profile.
    const me = await fetchUser();
    return me ? { ...response, user: { ...response.user, ...me } } : response;
  };

  const login = async (email, password) => completeLogin(await api.login(email, password), password);

  const register = async (username, email, password, profile, options) => {
    const response = await api.register(username, email, password, profile, options);
    const token = response.access_token || response.token;
    if (token) {
      api.setToken(token);
      localStorage.setItem('token', token);
    }
    if (token) {
      await fetchUser();
    }
    return response;
  };

  // Unified logout. scope='global' (default) ends every session for the user;
  // scope='app' ends only this app's session. Local state is always cleared and
  // other tabs are notified. If the server did not confirm, the person is told:
  // the sessions it was asked to end may still be live. A 401 is not that — the
  // session was already over.
  const logout = useCallback(async ({ scope = 'global' } = {}) => {
    try {
      await api.logout(scope);
    } catch (error) {
      if (!error?.isAuthError) {
        toast.error(scope === 'global'
          ? 'Signed out here, but the server didn’t confirm it. Other places may still be signed in: sign in and try again.'
          : 'Signed out here, but the server didn’t confirm it. This session may still be live: sign in and try again.', 10000);
      }
    } finally {
      clearLocalAuth();
      try { authChannel?.postMessage({ type: 'logout' }); } catch (e) { /* no-op */ }
    }
  }, [clearLocalAuth, toast]);

  const isAdmin = !!(user && user.is_admin);

  const hasAccess = useCallback((scope) => {
    if (!user) return false;
    if (user.is_admin) return true;
    return user.access?.[scope] !== false;
  }, [user]);

  const clearPendingRecoveryCodes = () => { setPendingRecoveryCodes(null); keepCodes(null); };

  const value = {
    user,
    loading,
    isAdmin,
    hasAccess,
    encKeys,   // { publicKey, privateKey, wrappingKey }
    unlockEncKeys,               // (password) => keys|null — re-derive encKeys on a resumed session
    pendingRecoveryCodes,        // plaintext codes shown once after first-login key setup
    recoveryCodesHaveEscrow,     // false → Settings should prompt user to regenerate codes
    clearPendingRecoveryCodes,
    login,
    completeLogin,               // (loginResponse, password?) — a sign-in answered elsewhere (the phone step)
    register,
    logout,
    refreshUser: fetchUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
